import {
  Box,
  Checkbox,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MdEdit, MdVisibility } from "react-icons/md";
import { orderBy as helperOrderBy } from "helpers/orderBy";
import { ArticleLocation, Client } from "types";
import { MOVEMENT_ARTICLES_URL } from "constants/urls";
import useCrud from "hooks/useCrud";
import { Order } from "../../../constants/index";
import TransferReservedMovementsDialog from "../TransferReservedMovementsDialog";
import TransferStateEditorDialog from "../TransferStateEditorDialog";
import {
  ArticleLocationRow,
  EnhancedTableProps as ImportedEnhancedTableProps,
  InventoryStatus,
  InventoryStateTarget,
  Props,
  ReservedMovementRelation,
  TransferTableHead,
  TransferOriginFilters,
} from "./types";
import {
  buildArticleLocationRows,
  buildTransferOriginRows,
  // buildTransferSearchKey,
  getArticleLocationSelectionStatus,
  mapRowToSelectedArticleLocation,
  renderTransferCellById,
  toNumber,
} from "./helpers";
import ArticleLocationFilters from "components/ArticleLocationFilters";

const headCells: readonly TransferTableHead[] = [
  { id: "location_id", numeric: false, disablePadding: true, label: "Ubicacion" },
  { id: "article_id", numeric: false, disablePadding: true, label: "Articulo (Cod/Desc)" },
  { id: "fecha_ingreso", numeric: false, disablePadding: true, label: "Ingreso" },
  { id: "amount_per_container", numeric: true, disablePadding: true, label: "Cant x Cont." },
  { id: "lote", numeric: false, disablePadding: true, label: "Lote" },
  { id: "oc", numeric: false, disablePadding: true, label: "OC" },
  { id: "fecha_vencimiento", numeric: false, disablePadding: true, label: "Vencimiento" },
  { id: "quantity", numeric: true, disablePadding: true, label: "Cantidad" },
  { id: "reserved_quantity", numeric: true, disablePadding: true, label: "Un. Reserv." },
  { id: "status_label", numeric: false, disablePadding: true, label: "Estado" },
  { id: "state_action", numeric: false, disablePadding: true, label: "Estados", sortable: false },
  { id: "reserved_detail", numeric: false, disablePadding: true, label: "Mov. Relac.", sortable: false },
  { id: "observation", numeric: false, disablePadding: true, label: "Observacion" },
];

type EnhancedTableProps = ImportedEnhancedTableProps & {
  headCells: readonly TransferTableHead[];
};

type ObservationCellProps = {
  onOpen: () => void;
};

const EnhancedTableHead = React.memo(
  ({ order, orderBy, onRequestSort, headCells: visibleHeadCells }: EnhancedTableProps) => {
    const createSortHandler = useCallback(
      (property: keyof ArticleLocationRow) => (event: React.MouseEvent<unknown>) => {
        onRequestSort(event, property);
      },
      [onRequestSort]
    );

    return (
      <TableHead>
        <TableRow>
          <TableCell />
          {visibleHeadCells.map((headCell) => (
            <TableCell
              key={headCell.id}
              align={headCell.numeric ? "right" : "left"}
              sortDirection={orderBy === headCell.id ? order : false}
            >
              {headCell.sortable === false ? (
                headCell.label
              ) : (
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : "asc"}
                  onClick={createSortHandler(headCell.id)}
                >
                  {headCell.label}
                </TableSortLabel>
              )}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    );
  }
);

const ObservationCell = React.memo(({ onOpen }: ObservationCellProps) => (
  <TableCell align="center" onClick={(event) => event.stopPropagation()}>
    <Tooltip title="Ver y editar observaciones" arrow>
      <Box component="span">
        <IconButton
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            onOpen();
          }}
        >
          <MdVisibility />
        </IconButton>
      </Box>
    </Tooltip>
  </TableCell>
));

const TransferLocationTable = ({
  tableType,
  articleLocations,
  transferOriginRows = [],
  // searchTerm: controlledSearchTerm,
  // onSearchChange,
  originFilters = {
    locationId: undefined,
    articleCode: "",
    lote: "",
    oc: "",
  },
  onOriginFiltersChange,
  filtersDisabled = false,
  onLoadMore,
  hasMore = false,
  loading = false,
  loadingMore = false,
  transferStock,
  setTransferStock,
  transfer,
  selectedOrigins = [],
  handleChangeMovement,
  client,
  onOpenObservationRecords,
  onRequestChangeState,
  preferredOrigin,
  onFromRowSelected,
  onSelectedOriginsChange,
}: Props) => {
  const [order, setOrder] = useState<Order>(Order.Descending);
  const [orderBy, setOrderBy] = useState<keyof ArticleLocationRow>("fecha_ingreso");
  // const [searchTerm, setSearchTerm] = useState<string>("");
  // const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [stateEditorRow, setStateEditorRow] = useState<ArticleLocationRow | null>(null);
  const [stateTargetStatus, setStateTargetStatus] = useState<InventoryStateTarget>("unavailable");
  const [stateQuantity, setStateQuantity] = useState<number>(1);
  const [stateSubmitting, setStateSubmitting] = useState<boolean>(false);
  const [reservedDetailRow, setReservedDetailRow] = useState<ArticleLocationRow | null>(
    null
  );
  const {
    data: reservedMovementRelations,
    loading: reservedMovementRelationsLoading,
    fetchData: getReservedMovementRelations,
    setData: setReservedMovementRelations,
  } = useCrud<ReservedMovementRelation>(MOVEMENT_ARTICLES_URL, {
    disableAutoFetch: true,
  });

  const isVisible = useCallback(
    (id: keyof ArticleLocationRow, selectedClient?: Client | null) => {
      switch (id) {
        case "lote":
          return !!selectedClient?.show_lote;
        case "oc":
          return !!selectedClient?.show_oc;
        case "fecha_vencimiento":
          return !!selectedClient?.show_fecha_vencimiento;
        case "state_action":
        case "reserved_detail":
        case "observation":
          return tableType === "from";
        default:
          return true;
      }
    },
    [tableType]
  );

  const visibleHeadCells = useMemo(
    () => headCells.filter((cell) => isVisible(cell.id as keyof ArticleLocationRow, client)),
    [client, isVisible]
  );

  const articleLocationRows = useMemo(
    () =>
      tableType === "from"
        ? buildTransferOriginRows(transferOriginRows)
        : buildArticleLocationRows(articleLocations),
    [articleLocations, tableType, transferOriginRows]
  );

  const getActualReservedQuantity = useCallback(
    (row: ArticleLocationRow) =>
      Math.max(
        0,
        toNumber(articleLocations.find((item) => item.id === row.id)?.reserved)
      ),
    [articleLocations]
  );

  const getActualAvailableQuantity = useCallback(
    (row: ArticleLocationRow) => {
      const originalRow = articleLocations.find((item) => item.id === row.id);

      if (!originalRow) return 0;

      return Math.max(
        0,
        toNumber(originalRow.stock) -
          toNumber(originalRow.reserved) -
          toNumber(originalRow.not_available) -
          toNumber(originalRow.in_work_orders)
      );
    },
    [articleLocations]
  );

  const getActualUnavailableQuantity = useCallback(
    (row: ArticleLocationRow) =>
      Math.max(
        0,
        toNumber(articleLocations.find((item) => item.id === row.id)?.not_available)
      ),
    [articleLocations]
  );

  const getSelectionStatus = useCallback(
    (selected?: ArticleLocation | null): InventoryStatus =>
      getArticleLocationSelectionStatus(selected),
    []
  );

  const isSameSelection = useCallback(
    (selected: ArticleLocation | null | undefined, row: ArticleLocationRow) =>
      !!selected &&
      selected.id === row.id &&
      getSelectionStatus(selected) === row.status,
    [getSelectionStatus]
  );

  const isSelectedOrigin = useCallback(
    (row: ArticleLocationRow) =>
      selectedOrigins.some((origin) => isSameSelection(origin, row)),
    [isSameSelection, selectedOrigins]
  );

  const handleRequestSort = useCallback(
    (_event: React.MouseEvent<unknown>, property: keyof ArticleLocationRow) => {
      const isAsc = orderBy === property && order === Order.Ascending;
      setOrder(isAsc ? Order.Descending : Order.Ascending);
      setOrderBy(property);
    },
    [order, orderBy]
  );

  const handleClick = useCallback(
    (_event: React.MouseEvent<unknown>, row: ArticleLocationRow | null) => {
      if (row) {
        const selectedArticleLocation = mapRowToSelectedArticleLocation(row);

        if (tableType === "from") {
          onFromRowSelected?.(row);

          const nextOrigins = isSelectedOrigin(row)
            ? selectedOrigins.filter((origin) => !isSameSelection(origin, row))
            : [...selectedOrigins, selectedArticleLocation];

          onSelectedOriginsChange?.(nextOrigins);
          handleChangeMovement("transfer", {
            ...(transfer ?? {}),
            from: nextOrigins.length > 0 ? nextOrigins[nextOrigins.length - 1] : null,
            to: null,
            amount: transfer?.amount ?? 0,
            new_location_id: transfer?.new_location_id ?? null,
            destinations: [],
            stock_unit_ids: undefined,
          });
          setTransferStock({ isTotal: null, value: 0 });
        } else {
          if (!transfer?.from) return;
          handleChangeMovement("transfer", {
            ...(transfer ?? {}),
            to: selectedArticleLocation,
          });
        }
        return;
      }

      handleChangeMovement("transfer", {
        ...(transfer ?? {}),
        from: null,
        to: null,
        amount: 0,
        new_location_id: null,
        destinations: [],
        stock_unit_ids: undefined,
      });
      setTransferStock({ isTotal: null, value: 0 });
    },
    [
      tableType,
      handleChangeMovement,
      transfer,
      setTransferStock,
      onFromRowSelected,
      selectedOrigins,
      isSelectedOrigin,
      isSameSelection,
      onSelectedOriginsChange,
    ]
  );

  const handleOpenReservedRelations = useCallback(
    async (row: ArticleLocationRow) => {
      setReservedDetailRow(row);
      setReservedMovementRelations([]);
      await getReservedMovementRelations(`reserved-relations/${row.id}`, true);
    },
    [getReservedMovementRelations, setReservedMovementRelations]
  );

  const handleCloseReservedRelations = useCallback(() => {
    setReservedDetailRow(null);
    setReservedMovementRelations([]);
  }, [setReservedMovementRelations]);

  const handleOpenStateEditor = useCallback(
    (row: ArticleLocationRow) => {
      const defaultTargetStatus: InventoryStateTarget =
        row.status === "available" ? "unavailable" : "available";
      const maxEditable =
        defaultTargetStatus === "unavailable"
          ? getActualAvailableQuantity(row)
          : getActualUnavailableQuantity(row);

      setStateEditorRow(row);
      setStateTargetStatus(defaultTargetStatus);
      setStateQuantity(maxEditable > 0 ? 1 : 0);
    },
    [getActualAvailableQuantity, getActualUnavailableQuantity]
  );

  const handleCloseStateEditor = useCallback(() => {
    if (stateSubmitting) {
      return;
    }

    setStateEditorRow(null);
    setStateTargetStatus("unavailable");
    setStateQuantity(1);
  }, [stateSubmitting]);

  // const searchable = useMemo(
  //   () =>
  //     articleLocationRows.map((al) => {
  //       return {
  //         al,
  //         searchKey: buildTransferSearchKey(al),
  //       };
  //     }),
  //   [articleLocationRows]
  // );

  // const isRemoteSearch = Boolean(onSearchChange);

  // useEffect(() => {
  //   const timeoutId = window.setTimeout(() => {
  //     setDebouncedSearchTerm(searchTerm);
  //   }, 250);

  //   return () => window.clearTimeout(timeoutId);
  // }, [searchTerm]);

  // useEffect(() => {
  //   if (controlledSearchTerm === undefined) {
  //     return;
  //   }

  //   setSearchTerm(controlledSearchTerm);
  // }, [controlledSearchTerm]);

  // const handleSearchTermChange = useCallback(
  //   (event: React.ChangeEvent<HTMLInputElement>) => {
  //     const nextSearchTerm = event.target.value;
  //     setSearchTerm(nextSearchTerm);
  //     onSearchChange?.(nextSearchTerm);
  //   },
  //   [onSearchChange]
  // );

  const handleOriginFiltersChange = useCallback(
    (nextFilters: TransferOriginFilters) => {
      onOriginFiltersChange?.(nextFilters);
    },
    [onOriginFiltersChange],
  );

  const filteredArticleLocations = useMemo(() => {
    // const searchLower = isRemoteSearch ? "" : debouncedSearchTerm.trim().toLowerCase();
    // const base = searchLower
    //   ? searchable.filter((item) => item.searchKey.includes(searchLower)).map((item) => item.al)
    //   : articleLocationRows;

    // return helperOrderBy(base, orderBy, order);
    return helperOrderBy(articleLocationRows, orderBy, order);
  }, [articleLocationRows, orderBy, order]);

  const handleTableScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (!onLoadMore || !hasMore || loadingMore) {
        return;
      }

      const target = event.currentTarget;
      const reachedBottom =
        target.scrollTop + target.clientHeight >= target.scrollHeight - 24;

      if (reachedBottom) {
        onLoadMore();
      }
    },
    [hasMore, loadingMore, onLoadMore],
  );

  const reservedDetailReservedQuantity = useMemo(
    () => (reservedDetailRow ? getActualReservedQuantity(reservedDetailRow) : 0),
    [getActualReservedQuantity, reservedDetailRow]
  );

  const stateEditorAvailableQuantity = useMemo(
    () => (stateEditorRow ? getActualAvailableQuantity(stateEditorRow) : 0),
    [getActualAvailableQuantity, stateEditorRow]
  );

  const stateEditorUnavailableQuantity = useMemo(
    () => (stateEditorRow ? getActualUnavailableQuantity(stateEditorRow) : 0),
    [getActualUnavailableQuantity, stateEditorRow]
  );

  const stateEditorMaxEditable = useMemo(() => {
    if (!stateEditorRow) return 0;

    return stateTargetStatus === "unavailable"
      ? stateEditorAvailableQuantity
      : stateEditorUnavailableQuantity;
  }, [
    stateEditorAvailableQuantity,
    stateEditorRow,
    stateEditorUnavailableQuantity,
    stateTargetStatus,
  ]);

  const handleApplyStateChange = useCallback(async () => {
    if (!stateEditorRow || stateQuantity <= 0 || stateQuantity > stateEditorMaxEditable) {
      return;
    }

    setStateSubmitting(true);
    const wasUpdated = await onRequestChangeState({
      id: stateEditorRow.id,
      targetStatus: stateTargetStatus,
      quantity: stateQuantity,
    });
    setStateSubmitting(false);

    if (wasUpdated) {
      handleCloseStateEditor();
    }
  }, [
    handleCloseStateEditor,
    onRequestChangeState,
    stateEditorMaxEditable,
    stateEditorRow,
    stateQuantity,
    stateTargetStatus,
  ]);

  useEffect(() => {
    const visibleIds = new Set(visibleHeadCells.map((cell) => cell.id));
    if (!visibleIds.has(orderBy)) {
      setOrderBy((visibleHeadCells[0]?.id as keyof ArticleLocationRow) ?? "fecha_ingreso");
    }
  }, [visibleHeadCells, orderBy]);

  useEffect(() => {
    if (!stateEditorRow) return;

    setStateQuantity((current) => {
      if (stateEditorMaxEditable <= 0) return 0;
      if (current < 1 || current > stateEditorMaxEditable) return 1;
      return current;
    });
  }, [stateEditorMaxEditable, stateEditorRow, stateTargetStatus]);

  useEffect(() => {
    if (tableType !== "from") return;
    if (!preferredOrigin) return;
    if (transfer?.from) return;

    const matchingRow = articleLocationRows.find(
      (row) =>
        row.id === preferredOrigin.id &&
        row.quantity > 0 &&
        getArticleLocationSelectionStatus(row) === preferredOrigin.status
    );

    const selectedArticleLocation = matchingRow ? mapRowToSelectedArticleLocation(matchingRow) : null;
    

    handleChangeMovement("transfer", {
      ...(transfer ?? {}),
      from: selectedArticleLocation,
      to: null,
      amount: 0,
      new_location_id: transfer?.new_location_id ?? null,
      stock_unit_ids: undefined,
      destinations: [],
    });

    setTransferStock({ isTotal: null, value: 0 });
  }, [
      articleLocationRows,
      handleChangeMovement,
      transfer,
      preferredOrigin,
      setTransferStock,
      tableType,
    ]);

  return (
    <Grid item xs={12}>
      <Box width="100%" pt={5}>
        <Typography variant="h6">
          {tableType === "to" ? "Hasta" : tableType === "from" ? "Desde" : ""}
        </Typography>
        {/* <TextField
          label="Buscar ubicacion o articulo"
          variant="outlined"
          fullWidth
          margin="normal"
          value={searchTerm}
          onChange={handleSearchTermChange}
        /> */}
        {tableType === "from" && (
          <ArticleLocationFilters
            clientId={client?.id}
            value={originFilters}
            onChange={handleOriginFiltersChange}
            disabled={filtersDisabled}
          />
        )}
      </Box>

      <TableContainer component={Paper} sx={{ maxHeight: 240 }} onScroll={handleTableScroll}>
        <Table stickyHeader>
          <EnhancedTableHead
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={filteredArticleLocations.length}
            headCells={visibleHeadCells}
          />
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={visibleHeadCells.length + 1} sx={{ p: 0 }}>
                  <Box
                    height={190}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    color="text.secondary"
                  >
                    <CircularProgress color="inherit" />
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              filteredArticleLocations.map((al, index) => {
              const selectedFrom = transfer?.from ?? null;
              const selectedTo = transfer?.to ?? null;
              const isItemSelected =
                tableType === "from"
                  ? isSelectedOrigin(al)
                  : isSameSelection(selectedTo, al);
              const isSelectedInFrom = isSameSelection(selectedFrom, al);
              const canSelect = al.quantity > 0;
              const isDisabled =
                !canSelect ||
                (tableType === "to" && (isSelectedInFrom || !transfer?.from?.id));
              const labelId = `article-location-${index}`;

              return (
                <TableRow
                  hover={!isDisabled}
                  onClick={(event: React.MouseEvent<unknown>) => {
                    if (isDisabled) {
                      return;
                    }

                    if (isItemSelected && tableType !== "from") {
                      handleClick(event, null);
                      return;
                    }

                    handleClick(event, al);
                  }}
                  role="checkbox"
                  aria-checked={isItemSelected}
                  tabIndex={-1}
                  key={al.row_key}
                  selected={isItemSelected}
                  sx={(theme) => ({
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    opacity: canSelect ? 1 : 0.7,
                    td: {
                      color: isDisabled ? theme.palette.text.disabled : undefined,
                    },
                  })}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      checked={isItemSelected}
                      disabled={isDisabled}
                      inputProps={{
                        "aria-labelledby": labelId,
                      }}
                    />
                  </TableCell>

                  {visibleHeadCells.map((col) => {
                    const colId = col.id as keyof ArticleLocationRow;

                    if (colId === "state_action") {
                      if (al.status === "reserved") {
                        return <TableCell key={col.id} align="center">-</TableCell>;
                      }

                      return (
                        <TableCell key={col.id} align="center">
                          <Tooltip title="Editar estados de mercaderia" arrow>
                            <Box component="span">
                              <IconButton
                                size="small"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleOpenStateEditor(al);
                                }}
                              >
                                <MdEdit />
                              </IconButton>
                            </Box>
                          </Tooltip>
                        </TableCell>
                      );
                    }

                    if (colId === "reserved_detail") {
                      return (
                        <TableCell key={col.id} align="center">
                          <Tooltip
                            title="Movimientos relacionados a mercaderia reservada"
                            arrow
                          >
                            <Box component="span">
                              <IconButton
                                size="small"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  void handleOpenReservedRelations(al);
                                }}
                              >
                                <MdVisibility />
                              </IconButton>
                            </Box>
                          </Tooltip>
                        </TableCell>
                      );
                    }

                    if (colId === "observation") {
                      return (
                        <ObservationCell
                          key={col.id}
                          onOpen={() => onOpenObservationRecords(al)}
                        />
                      );
                    }

                    return (
                      <TableCell key={col.id} align={col.numeric ? "right" : "left"}>
                        {renderTransferCellById(colId, al)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
              })
            )}
            {loadingMore && (
              <TableRow>
                <TableCell colSpan={visibleHeadCells.length + 1} align="center">
                  Cargando...
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TransferReservedMovementsDialog
        open={Boolean(reservedDetailRow)}
        row={reservedDetailRow}
        reservedQuantity={reservedDetailReservedQuantity}
        relations={reservedMovementRelations}
        loading={reservedMovementRelationsLoading}
        onClose={handleCloseReservedRelations}
      />

      <TransferStateEditorDialog
        open={Boolean(stateEditorRow)}
        row={stateEditorRow}
        targetStatus={stateTargetStatus}
        quantity={stateQuantity}
        maxEditable={stateEditorMaxEditable}
        availableQuantity={stateEditorAvailableQuantity}
        unavailableQuantity={stateEditorUnavailableQuantity}
        submitting={stateSubmitting}
        onClose={handleCloseStateEditor}
        onTargetStatusChange={setStateTargetStatus}
        onQuantityChange={setStateQuantity}
        onApply={() => {
          void handleApplyStateChange();
        }}
      />
    </Grid>
  );
};

export default React.memo(TransferLocationTable);
